import { ConditionOperatorMap } from 'liyu-pc-base/api/typing';

/* ★ v29.20：ColumnSearchComponentModal 拆分产物。
   所有类型分组 / 类型默认值 / 运算符集集中于此（port 自 FormDesignModal/data.ts + defaultConfig，
   裁剪到搜索支持的范围），供各基础配置组件与分类型 Setting 组件共用。 */

/* ───────── 栅格约定（★ v29.31，改布局前先读） ─────────
   容器 = 右侧抽屉，宽 920px（ColumnSearchComponentModal 的 SEARCH_DRAWER_WIDTH）；
   表单标签列固定 190px（`:label-col="{ style: { width: '190px' } }"`）。

   于是每一列留给控件的宽度 ≈ 列宽 − 190px：
     · span=24 → 888 − 190 = 698px   （整行独占：长文本 / 表格 / 提示）
     · span=12 → 444 − 190 = 254px   （**每行 2 个控件**，本目录的默认密度）
     · span=8  → 296 − 190 = 106px   ← 已废弃：106px 装不下日期格式串/数值框，会被挤成两行
     · span=6 / span=4 → 更窄，同样废弃
   ⇒ **约定：一行最多 2 个控件（span=12），整行独占才用 24。** 新增配置项照此选 span；
     若调整 SEARCH_DRAWER_WIDTH 或标签列宽度，**回到这里重算一次**（本目录所有 :span 都是 12/24）。 */

/* ───────── 运算符（port 自 FieldSetting 的查询条件配置） ───────── */
export const OPERATOR: Record<string, string> = ConditionOperatorMap as any;
export const TEXT_OPS = ['eq', 'icontains', 'istartswith', 'iendswith', 'not_eq', 'not_blank_not_null', 'contains', 'startswith', 'endswith', 'noticontains', 'notistartswith', 'notiendswith', 'notcontains', 'notstartswith', 'notendswith'];
export const NUMBER_OPS = ['between', 'gt', 'lt', 'eq', 'gte', 'lte', 'notbetween', 'not_eq', 'not_blank_not_null'];
export const SELECT_OPS = ['in', 'notin'];
export const DATE_OPS = ['between', 'eq', 'gt', 'lt', 'gte', 'lte', 'notbetween'];
export const SEARCH_CLASS_NAME = ['int', 'str', 'bool', 'datetime'];
export const FORMAT_TYPE = ['input', 'remote', 'choices'];
/* antdv select option 的 value 类型声明不含 boolean，这里放宽（布尔固定查询值需要 true/false） */
export const BOOL_OPTIONS: any[] = [
  { value: true, label: 'true' },
  { value: false, label: 'false' },
];

/* ───────── 组件类型分组 ───────── */
/** 本地扁平选项组件（options 数组） */
export const FLAT_OPTION_TYPES = ['select', 'radio', 'checkbox'];
/** ★ v29.29：本地静态选项类（选项列表在「数据源」页配置）——原版 FieldSetting 的
 *  「选项列表」编辑器覆盖范围就是这三类；groupselect 走 a-select 的 :options 直传
 *  （不认 valueKey/labelKey），同样需要本地 options，故一并纳入。 */
export const LOCAL_OPTION_TYPES = ['select', 'radio', 'checkbox', 'groupselect'];
/** FlatSelectSetting 覆盖的类型（FLAT_OPTION_TYPES + groupselect） */
export const FLAT_SETTING_TYPES = ['select', 'radio', 'checkbox', 'groupselect'];
/** 用 mode(default/multiple/tags) 表达单选多选的类型 */
export const MODE_TYPES = ['select', 'remoteselectpro', 'remotecomplete', 'remotetext'];
/** 用 multiple(bool) 表达单选多选的类型 */
export const MULTIPLE_TYPES = ['treeselect', 'cascader', 'remotetreeselectpro', 'remotecascaderpro'];
/** 选择类（运算符取 in/notin 的判定范围） */
export const SELECT_LIKE_TYPES = [...FLAT_OPTION_TYPES, 'treeselect', 'cascader', 'groupselect', 'remotefilterselect', ...MODE_TYPES, ...MULTIPLE_TYPES];
/** 支持 maxlength / 前后缀标签的类型 */
export const TEXT_WITH_ADDON_TYPES = ['input', 'number'];
/** 数值范围配置的类型 */
export const NUMBER_TYPES = ['number', 'money'];
/** 日期时间类 */
export const DATE_TYPES = ['datepicker', 'monthpicker', 'monthrangepicker', 'timepicker', 'timerangepicker', 'rangepicker'];
/** placeholder 为 [开始, 结束] 区间双输入的类型 */
export const RANGE_PLACEHOLDER_TYPES = ['rangepicker', 'timerangepicker', 'monthrangepicker'];
/** 远程数据源类（数据源页适用） */
export const REMOTE_TYPES = ['remoteselectpro', 'remotecomplete', 'remotetreeselectpro', 'remotecascaderpro', 'remotetext'];

/* ───────── 可选组件类型（SmallTableSerarchField 支持且适合搜索的子集，按 defaultConfig 分组） ───────── */
export const typeOptions = [
  {
    label: '输入类',
    options: [
      { value: 'input', label: '单行文本框' },
      { value: 'number', label: '数字' },
      { value: 'money', label: '金额' },
      { value: 'remotetext', label: '远程文本' },
      { value: 'remotecomplete', label: '自动补全文本框' },
    ],
  },
  {
    label: '选择类',
    options: [
      { value: 'select', label: '下拉选框' },
      { value: 'radio', label: '单选框' },
      { value: 'checkbox', label: '多选框' },
      { value: 'treeselect', label: '树形下拉' },
      { value: 'cascader', label: '级联下拉' },
      { value: 'remoteselectpro', label: '远程下拉选框' },
      { value: 'remotetreeselectpro', label: '远程树形下拉' },
      { value: 'remotecascaderpro', label: '远程级联下拉' },
      { value: 'remotefilterselect', label: '远程筛选下拉' },
      { value: 'groupselect', label: '分组下拉' },
    ],
  },
  {
    label: '日期/时间',
    options: [
      { value: 'datepicker', label: '日期时间选择器' },
      { value: 'rangepicker', label: '日期时间区间' },
      { value: 'monthpicker', label: '月份选择器' },
      { value: 'monthrangepicker', label: '月份区间' },
      { value: 'timepicker', label: '时间选择器' },
      { value: 'timerangepicker', label: '时间区间' },
    ],
  },
];

/* ───────── 本地静态选项契约（v29.29） ───────── */
/** 选项是否有效：value 必须有真值语义（0 / false 合法，null / undefined / '' 视为未填）。
 *  供「选项列表」编辑与保存校验共用——空值选项在 a-select 里无法选中，落库即脏配置。 */
export const hasOptionValue = (o: any) =>
  !!o && o.value !== null && o.value !== undefined && o.value !== '';

/* ───────── 各类型默认属性（port 自 defaultConfig.ts；搜索场景 select 默认多选） ───────── */
export const DS_DEFAULTS: Record<string, any> = {
  labelKeyType: 1,
  labelKeyList: [],
  labelSpace: '-',
  valueKey: 'id',
  labelKey: 'name',
  queryType: 'advanced',
  searchConditions: [[{ searchType: 3, field_name: 'is_active', operator: 'eq', value: [true], format: 'choices', class_name: 'bool' }]],
  simpleSearchConditions: [],
};
export const REMOTE_BASE = { show_search: true, postUrl: '', ...DS_DEFAULTS };
export const TYPE_DEFAULTS: Record<string, any> = {
  input: { type: 'input', label: '单行文本框', inputType: 'text', maxlength: 100, allowClear: true, placeholder: '' },
  number: { type: 'number', label: '数字', inputType: 'number', precision: null, step: 1, allowClear: true, placeholder: '' },
  money: { type: 'money', label: '金额', precision: 2, allowClear: true, placeholder: '' },
  remotetext: { type: 'remotetext', label: '远程文本', mode: 'default', ...REMOTE_BASE },
  remotecomplete: { type: 'remotecomplete', label: '自动补全文本框', mode: 'default', ...REMOTE_BASE },
  /* ★ v29.29：本地静态选项类【不放 valueKey/labelKey】——选项对象统一为 `{value, label}`
     （lybase ADVANCED_SEARCH + choices 的唯一形状：`util/field_config.py` 产出 `options:[{label,value}]`
     且不写这两个键；后端 `_adv_search_item` 由 content_map 自动推断下拉时同样只写 `{value,label}`）。
     运行时 `SmallTableSerarchField.vue` 读的是 `option[valueKey||'value']` / `option[labelKey||'label']`，
     groupselect 更是把 options 直接丢给 a-select 的 `:options`（不认键）——所以一旦残留
     `labelKey:'name'`，`{value,label}` 的选项会整列渲染成空白。 */
  select: { type: 'select', label: '下拉选框', mode: 'multiple', show_search: false, options: [], allowClear: true, placeholder: '' },
  radio: { type: 'radio', label: '单选框', options: [] },
  checkbox: { type: 'checkbox', label: '多选框', options: [] },
  treeselect: { type: 'treeselect', label: '树形下拉选框', multiple: false, show_search: false, valueKey: 'value', labelKey: 'name', options: [], allowClear: true, placeholder: '' },
  cascader: { type: 'cascader', label: '级联下拉选框', multiple: false, show_search: false, valueKey: 'value', labelKey: 'name', options: [], allowClear: true, placeholder: '' },
  remotefilterselect: { type: 'remotefilterselect', label: '远程筛选下拉', ...REMOTE_BASE },
  groupselect: { type: 'groupselect', label: '分组下拉', show_search: false, options: [], allowClear: true, placeholder: '' },
  remoteselectpro: { type: 'remoteselectpro', label: '远程下拉选框', mode: 'default', ...REMOTE_BASE },
  remotetreeselectpro: { type: 'remotetreeselectpro', label: '远程树形下拉框', multiple: false, ...REMOTE_BASE },
  remotecascaderpro: { type: 'remotecascaderpro', label: '远程级联下拉框', multiple: false, show_search: false, ...REMOTE_BASE },
  datepicker: { type: 'datepicker', label: '日期时间选择器', showTime: true, format: 'YYYY-MM-DD HH:mm:ss', valueFormat: 'YYYY-MM-DD HH:mm:ss', allowClear: true, placeholder: '' },
  rangepicker: { type: 'rangepicker', label: '日期时间区间', showTime: true, format: 'YYYY-MM-DD HH:mm:ss', valueFormat: 'YYYY-MM-DD HH:mm:ss', placeholder: ['开始时间', '结束时间'], allowClear: true },
  monthpicker: { type: 'monthpicker', label: '月份选择器', showTime: false, format: 'YYYY-MM', valueFormat: 'YYYY-MM-DD', allowClear: true, placeholder: '' },
  monthrangepicker: { type: 'monthrangepicker', label: '月份区间', picker: 'month', showTime: false, format: 'YYYY-MM', valueFormat: 'YYYY-MM-DD', placeholder: ['开始月份', '结束月份'], allowClear: true },
  timepicker: { type: 'timepicker', label: '时间选择器', format: 'HH:mm:ss', valueFormat: 'HH:mm:ss', allowClear: true, placeholder: '' },
  timerangepicker: { type: 'timerangepicker', label: '时间区间', format: 'HH:mm:ss', valueFormat: 'HH:mm:ss', placeholder: ['开始时间', '结束时间'], allowClear: true },
};
